/**
 * Smart unit error detection service
 * Detects when a value is likely mislabeled with wrong units by comparing to other values in the series
 */

interface DataPoint {
  date: string;
  value: number;
  units: string;
}

interface UnitError {
  index: number;
  currentValue: number;
  currentUnits: string;
  suggestedValue: number;
  suggestedUnits: string;
  confidence: 'high' | 'medium' | 'low';
  reason: string;
}

// Common unit conversion factors
const UNIT_CONVERSIONS: Record<string, Record<string, number>> = {
  'g/dL_to_g/L': { factor: 10 },
  'g/L_to_g/dL': { factor: 0.1 },
  'mg/dL_to_mmol/L': { factor: 0.0555 }, // for glucose
  'mmol/L_to_mg/dL': { factor: 18.02 }, // for glucose
};

// Expected ranges for common tests
const EXPECTED_RANGES: Record<string, Record<string, { min: number; max: number }>> = {
  'MCHC': {
    'g/dL': { min: 30, max: 38 },
    'g/L': { min: 300, max: 380 },
  },
  'Hemoglobin': {
    'g/dL': { min: 8, max: 20 },
    'g/L': { min: 80, max: 200 },
  },
  'Haemoglobin': {  // Alternative spelling
    'g/dL': { min: 8, max: 20 },
    'g/L': { min: 80, max: 200 },
  },
  'Glucose': {
    'mg/dL': { min: 50, max: 400 },
    'mmol/L': { min: 2.8, max: 22.2 },
  },
  'Creatinine': {
    'mg/dL': { min: 0.4, max: 2.5 },
    'umol/L': { min: 35, max: 220 },
  },
  'WBC': {
    '10^9/L': { min: 3.5, max: 11 },
    'E9/L': { min: 3.5, max: 11 }, // Same as 10^9/L
    'K/uL': { min: 3.5, max: 11 },
  },
  'MCV': {
    'fL': { min: 80, max: 100 },
    'E': { min: 80, max: 100 }, // Sometimes mislabeled as just 'E'
  },
  'Testosterone': {
    'nmol/L': { min: 8, max: 35 }, // Adult male range
    'ng/dL': { min: 230, max: 1000 }, // Adult male range
  },
};

// Check if two units are equivalent (just different notation)
function areUnitsEquivalent(unit1: string, unit2: string): boolean {
  // E9/L and 10^9/L are the same
  if ((unit1 === 'E9/L' && unit2 === '10^9/L') || 
      (unit1 === '10^9/L' && unit2 === 'E9/L')) {
    return true;
  }
  
  // Case variations of the same unit
  if (unit1.toLowerCase() === unit2.toLowerCase()) {
    return true;
  }
  
  return false;
}

// Get the standard form of a unit
function getStandardUnit(unit: string): string {
  // Standardize E notation
  if (unit === 'E9/L') return '10^9/L';
  
  // Fix common mislabelings
  if (unit === 'E' && EXPECTED_RANGES['MCV']) return 'fL';
  
  // Standardize case
  if (unit.toLowerCase() === 'nmol/l') return 'nmol/L';
  if (unit.toLowerCase() === 'ng/dl') return 'ng/dL';
  
  return unit;
}

export function detectUnitErrors(
  testName: string,
  dataPoints: DataPoint[]
): UnitError[] {
  const errors: UnitError[] = [];
  
  if (dataPoints.length < 2) return errors;

  // Group data points by their actual units (not normalized)
  const unitGroups = new Map<string, DataPoint[]>();
  dataPoints.forEach(dp => {
    // Check if this unit already has an equivalent group
    let foundGroup = false;
    unitGroups.forEach((points, existingUnit) => {
      if (!foundGroup && areUnitsEquivalent(dp.units, existingUnit)) {
        points.push(dp);
        foundGroup = true;
      }
    });
    
    if (!foundGroup) {
      unitGroups.set(dp.units, [dp]);
    }
  });

  // Find the most common unit group
  let mostCommonUnit = '';
  let maxCount = 0;
  unitGroups.forEach((points, unit) => {
    if (points.length > maxCount) {
      maxCount = points.length;
      mostCommonUnit = unit;
    }
  });
  
  // Standardize the most common unit
  mostCommonUnit = getStandardUnit(mostCommonUnit);
  
  // Only process if we have a clear pattern (more than 60% using one unit)
  if (maxCount < dataPoints.length * 0.6) {
    return errors; // No clear pattern, don't flag errors
  }

  // Check each data point
  dataPoints.forEach((dp, index) => {
    // Skip if this unit is equivalent to the most common
    if (areUnitsEquivalent(dp.units, mostCommonUnit)) {
      return;
    }
    
    // Check if the value fits the expected range for its claimed unit
    const expectedRange = EXPECTED_RANGES[testName]?.[dp.units];
    const mostCommonRange = EXPECTED_RANGES[testName]?.[mostCommonUnit];
    
    if (expectedRange && mostCommonRange) {
      const fitsCurrentUnit = dp.value >= expectedRange.min && dp.value <= expectedRange.max;
      const fitsMostCommonUnit = dp.value >= mostCommonRange.min && dp.value <= mostCommonRange.max;
      
      // If value doesn't fit its claimed unit but fits the most common unit
      if (!fitsCurrentUnit && fitsMostCommonUnit) {
        errors.push({
          index,
          currentValue: dp.value,
          currentUnits: dp.units,
          suggestedValue: dp.value,
          suggestedUnits: mostCommonUnit,
          confidence: 'high',
          reason: `Value ${dp.value} fits ${mostCommonUnit} range but is labeled as ${dp.units}`
        });
        return;
      }
      
      // Check if it needs conversion
      const conversionKey = `${dp.units}_to_${mostCommonUnit}`;
      const reverseConversionKey = `${mostCommonUnit}_to_${dp.units}`;
      let conversionFactor: number | undefined;
      
      if (UNIT_CONVERSIONS[conversionKey]) {
        conversionFactor = UNIT_CONVERSIONS[conversionKey].factor;
      } else if (UNIT_CONVERSIONS[reverseConversionKey]) {
        conversionFactor = 1 / UNIT_CONVERSIONS[reverseConversionKey].factor;
      }
      
      if (conversionFactor) {
        const convertedValue = dp.value * conversionFactor;
        const fitsAfterConversion = convertedValue >= mostCommonRange.min && convertedValue <= mostCommonRange.max;
        
        if (fitsAfterConversion) {
          errors.push({
            index,
            currentValue: dp.value,
            currentUnits: dp.units,
            suggestedValue: convertedValue,
            suggestedUnits: mostCommonUnit,
            confidence: 'high',
            reason: `Value needs conversion from ${dp.units} to ${mostCommonUnit}`
          });
        }
      }
    }
    
    // Special case for obvious errors (like "E" instead of "fL" for MCV)
    if (testName === 'MCV' && dp.units === 'E' && dp.value >= 80 && dp.value <= 100) {
      errors.push({
        index,
        currentValue: dp.value,
        currentUnits: dp.units,
        suggestedValue: dp.value,
        suggestedUnits: 'fL',
        confidence: 'high',
        reason: `MCV unit 'E' should be 'fL'`
      });
    }
  });

  return errors;
}

export function applyUnitCorrection(
  dataPoint: DataPoint,
  correction: UnitError
): DataPoint {
  return {
    ...dataPoint,
    value: correction.suggestedValue,
    units: correction.suggestedUnits,
  };
}